import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { CheckCircle, Clock3, Loader2, QrCode } from "lucide-react";
import { Button } from "./ui/button";
import { trpc } from "../lib/trpc";

type TipoAssinaturaPublicaQr = "EXPEDICAO" | "DEVOLUCAO" | "ROMANEIO";

type PublicSignatureQrProps = {
  tipo: TipoAssinaturaPublicaQr;
  titulo: string;
  descricao: string;
  referencia: Record<string, unknown>;
  onSigned: (dados: {
    nomeAssinante: string;
    documentoAssinante: string;
    placaVeiculo: string | null;
    whatsapp: string | null;
    assinaturaBase64: string;
  }) => void;
};

export function PublicSignatureQr({ tipo, titulo, descricao, referencia, onSigned }: PublicSignatureQrProps) {
  const [token, setToken] = useState<string | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string>("");
  const criandoRef = useRef(false);
  const criarMutation = trpc.assinaturaPublica.criar.useMutation({
    onSuccess: (sessao) => setToken(sessao.token),
    onError: () => { criandoRef.current = false; },
  });
  const statusQuery = trpc.assinaturaPublica.status.useQuery(
    { token: token || "0".repeat(48) },
    { enabled: !!token, refetchInterval: 5000, retry: false },
  );

  useEffect(() => {
    if (!token && !criandoRef.current) {
      criandoRef.current = true;
      criarMutation.mutate({ tipo, titulo, descricao, referencia });
    }
  }, [token, tipo, titulo, descricao, referencia]);

  useEffect(() => {
    if (!token) return;
    const url = `${window.location.origin}/assinatura-publica/${token}`;
    QRCode.toDataURL(url, { width: 280, margin: 1, errorCorrectionLevel: "M" })
      .then(setQrDataUrl)
      .catch(() => setQrDataUrl(""));
  }, [token]);

  useEffect(() => {
    const sessao = statusQuery.data;
    if (sessao?.status !== "ASSINADO") return;
    onSigned({
      nomeAssinante: sessao.nomeAssinante,
      documentoAssinante: sessao.documentoAssinante,
      placaVeiculo: sessao.placaVeiculo || null,
      whatsapp: sessao.whatsapp || null,
      assinaturaBase64: sessao.assinaturaBase64,
    });
  }, [statusQuery.data, onSigned]);

  const gerarNovo = () => {
    setQrDataUrl("");
    criandoRef.current = false;
    setToken(null);
  };

  if (criarMutation.isError || statusQuery.isError) {
    return <p className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">Não foi possível gerar ou consultar o QR de assinatura.</p>;
  }

  if (statusQuery.data?.status === "ASSINADO") {
    return <div className="flex items-center gap-2 rounded border border-green-200 bg-green-50 p-3 text-sm text-green-700"><CheckCircle className="h-4 w-4" />Assinatura recebida. Confira os dados e confirme o fluxo.</div>;
  }

  if (statusQuery.data?.status === "EXPIRADO") {
    return (
      <div className="space-y-3 rounded border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
        <div className="flex items-center gap-2 font-medium"><Clock3 className="h-4 w-4" />QR expirado.</div>
        <p>Gere um novo QR para continuar.</p>
        <Button type="button" variant="outline" onClick={gerarNovo}>Gerar novo QR</Button>
      </div>
    );
  }

  if (!qrDataUrl || statusQuery.isLoading) {
    return <div className="flex min-h-48 items-center justify-center gap-2 rounded border bg-muted/30 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Gerando QR de assinatura...</div>;
  }

  return (
    <div className="rounded border bg-slate-50 p-4 text-center">
      <div className="mb-3 flex items-center justify-center gap-2 text-sm font-medium"><QrCode className="h-4 w-4" />Assinatura pelo celular</div>
      <img src={qrDataUrl} alt="QR Code para assinatura pública" className="mx-auto h-56 w-56 rounded bg-white p-2" />
      <p className="mt-3 text-xs text-muted-foreground">Escaneie o QR com o celular. Ele vale por 5 minutos e a tela será atualizada automaticamente após a assinatura.</p>
    </div>
  );
}
